import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import RadioSelect from '@/components/RadioSelectOptions';
import { SelectReferralSource } from '@/components/SelectReferralSource';
import { InviteClientHeader } from '@/components/invite-client//InviteClientHeader';
import { SelectClientRelationshipStage } from '@/components/SelectClientRelationshipStage';
import { InviteClientProgressBar } from '@/components/invite-client/InviteClientProgressBar';

import { ChevronLeft, Send } from 'lucide-react';

import { useInviteClientContext } from '@/components/invite-client/InviteClientContext';

const shareResultOptions = [
  {
    label: 'Yes, share immediately',
    description: 'Client receives results link after completing assessment',
    value: 'yes',
    id: 'yes-client-results',
  },
  {
    label: "No, I'll review first",
    description: 'Review results before deciding what to share',
    value: 'no',
    id: 'no-client-results',
  },
];

export function InviteClientAdditionalSettings({ mode = 'email' }: { mode?: 'email' | 'link' }) {
  const {
    inviteClientConfig,
    isLoading,
    inviteCount,
    isBulkInvite,
    handleStepChange,
    handleSendEmail,
    handleConfigChange,
    referralSources,
    setReferralSources,
  } = useInviteClientContext();

  const isModeEmail = mode === 'email';

  const { shareResults, clientRelationship, referralSource, isComplianceConfirmed } =
    inviteClientConfig;

  function handleBack() {
    handleStepChange(isModeEmail ? 'customize-email' : 'link');
  }

  return (
    <>
      {isModeEmail && (
        <InviteClientProgressBar
          currentStep={2}
          stepLabel="Customize"
        />
      )}

      <div className={!isModeEmail ? 'mt-2 pr-2' : ''}>
        <InviteClientHeader
          title="Additional Settings"
          action={
            <Button
              size="sm"
              variant="ghost"
              onClick={handleBack}
              className="ml-auto h-7 bg-gray-100 px-2 text-xs text-gray-950"
            >
              Back to {mode}
            </Button>
          }
          description="Configure how clients interact with their assessment"
          isOptional
        />
      </div>

      <div className="mt-4">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <Label className="text-sm font-medium text-gray-950">
              Should clients see their assessment results?
            </Label>
            <RadioSelect
              options={shareResultOptions}
              selectedValue={shareResults}
              onChange={value => handleConfigChange('shareResults', value)}
              className="tracking-normal"
              variant="box"
            />
          </div>

          {isModeEmail && (
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium text-gray-950">
                Client type <span className="text-gray-500">(optional)</span>
              </Label>
              <SelectClientRelationshipStage
                clientRelationshipStage={clientRelationship}
                onChange={value =>
                  handleConfigChange(
                    'clientRelationship',
                    value === 'undefined' ? undefined : value,
                  )
                }
                triggerClassName="text-gray-700"
              />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <SelectReferralSource
              selectedReferralSourceId={referralSource}
              onChangeReferralSourceId={value => handleConfigChange('referralSource', value)}
              placeholderText="Undefined"
              includeFieldLabel={true}
              controlledReferralSources={referralSources}
              setControlledReferralSources={setReferralSources}
            />
          </div>
        </div>

        {isBulkInvite && (
          <div className="mt-4 flex items-start gap-3">
            <Checkbox
              id="compliance"
              checked={isComplianceConfirmed}
              onCheckedChange={checked => handleConfigChange('isComplianceConfirmed', !!checked)}
              className="translate-y-0.5"
            />
            <Label
              htmlFor="compliance"
              className="text-sm italic text-gray-700"
            >
              I confirm that I have either obtained compliance approval or understand this message
              may require compliance review. I acknowledge that sending this email is my
              responsibility.
            </Label>
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <Button
          variant="outline"
          onClick={handleBack}
        >
          <ChevronLeft className="size-4" />
          Back
        </Button>

        {isModeEmail && (
          <Button
            onClick={handleSendEmail}
            disabled={isLoading || (isBulkInvite && !isComplianceConfirmed)}
          >
            <Send className="size-4" />
            {isLoading ? 'Sending...' : `Send ${inviteCount} invite${inviteCount > 1 ? 's' : ''}`}
          </Button>
        )}
      </div>
    </>
  );
}
